import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Headphones, Loader2, Pause, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { API, useAuth } from "../App";
import { useLanguage } from "../i18n/LanguageContext";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true });
const Ctx = createContext(null);
const src = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// Per-page cache of generated voice clips. Mounted by PageSectionEditor so every block on the page can ask for its clip.
export const TtsProvider = ({ layoutKey, children }) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [voices, setVoices] = useState({ voices: [], default: "echo" });
  const optedIn = !!user?.ai_opt_in;
  const load = useCallback(() => {
    if (!optedIn || !layoutKey) return;
    axios.get(`${API}/tts/for-page`, { params: { key: layoutKey, lang } }).then((r) => setClips(r.data || [])).catch(() => {});
  }, [layoutKey, lang, optedIn]);
  useEffect(load, [load]);
  useEffect(() => { if (optedIn) axios.get(`${API}/tts/voices`).then((r) => setVoices(r.data)).catch(() => {}); }, [optedIn]);
  const value = useMemo(() => ({ layoutKey, lang, clips, voices, optedIn, reload: load }), [layoutKey, lang, clips, voices, optedIn, load]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// One shared <audio> so only one clip plays at a time.
let current = null;

// Listen (everyone who opted in) · Generate / Redo / Remove (admin). Renders nothing for users who haven't opted into AI.
export const ListenButton = ({ blockId, section = null, label = "Listen", className = "", accent = "#2E5C55", dark = false }) => {
  const ctx = useContext(Ctx);
  const { user } = useAuth();
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(null);
  const audioRef = useRef(null);
  const isAdmin = user?.role === "admin" || user?.role === "magician";
  if (!ctx || !ctx.optedIn) return null;
  const clip = ctx.clips.find((c) => c.block_id === blockId && (c.section ?? null) === section);
  const tid = `${blockId}-${section ?? "all"}`;

  const toggle = () => {
    if (!clip) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(src(clip.url));
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onpause = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); return; }
    if (current && current !== audioRef.current) current.pause();
    current = audioRef.current; audioRef.current.play(); setPlaying(true);
  };
  const generate = async () => {
    const v = voice || ctx.voices.default;
    if (!window.confirm(`Generate a spoken version of this ${section === null ? "chapter" : "section"} with the ${v} voice? This uses your universal-key balance once; the clip is saved for everyone who opted in.`)) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/tts/generate`, { key: ctx.layoutKey, lang: ctx.lang, block_id: blockId, section, voice: v }, auth()); toast.success("Voice ready"); audioRef.current = null; ctx.reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this voice clip?")) return;
    await axios.delete(`${API}/admin/tts`, { params: { tid: clip.tid }, ...auth() }); audioRef.current = null; ctx.reload();
  };
  const base = `inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-black/5 text-[#1A1A1A] hover:bg-black/10"}`;

  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`} data-testid={`listen-${tid}`}>
      {clip && (
        <button type="button" onClick={toggle} className={base} style={{ color: dark ? undefined : accent }} data-testid={`listen-play-${tid}`}>
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Headphones className="h-3.5 w-3.5" />} {playing ? "Pause" : label}{clip.stale && isAdmin ? " · text changed" : ""}
        </button>
      )}
      {isAdmin && (
        <>
          {(!clip || clip.stale) && (
            <select value={voice || ctx.voices.default} onChange={(e) => setVoice(e.target.value)} className={`rounded-full px-2 py-1 text-[11px] ${dark ? "bg-white/10 text-white" : "bg-black/5 text-[#1A1A1A]"}`} aria-label="Voice" data-testid={`listen-voice-${tid}`}>
              {ctx.voices.voices.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          <button type="button" onClick={generate} disabled={busy} className={`${base} ${!clip ? "border border-dashed border-current" : ""}`} title={clip ? "Regenerate" : "Generate a spoken version (AI, opt-in)"} data-testid={`listen-generate-${tid}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : clip ? <RefreshCw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />} {busy ? "Generating…" : clip ? "Redo" : "Generate voice"}
          </button>
          {clip && <button type="button" onClick={remove} className={`${base} text-red-500`} aria-label="Remove voice clip" data-testid={`listen-remove-${tid}`}><Trash2 className="h-3.5 w-3.5" /></button>}
        </>
      )}
    </span>
  );
};
